import { FieldPath } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faArchive } from '@fortawesome/pro-solid-svg-icons';
import { archiveContact, editActivity } from 'api/contacts/single';
import { ARCHIVE_REASONS } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/archived/archiveReasons';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { ArchiveActivityParams, ArchiveReason, ArchivedActivity } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ArchiveContactProps extends AlertDialog.AlertDialogRootProps {
    activity?: ArchivedActivity;
}

type ArchiveContactForm = {
    reason: ArchiveReason;
    note: string;
};

const ArchiveContact = ({ children, activity, ...props }: ArchiveContactProps) => {
    const { key, forceRender } = useForceRender();
    const queryClient = useQueryClient();
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();

    const methods = Form.useGivebutterForm<ArchiveContactForm>({
        defaultValues: {
            reason: activity?.data.reason || 'inactivity',
            note: activity?.data.note,
        },
    });

    const { handleSubmit, path, control, setError } = methods;

    const { mutate } = useMutation({
        mutationFn: (data: ArchiveContactForm) => {
            if (activity?.id) {
                return editActivity<ArchiveActivityParams>({
                    accountId,
                    contactId: contact.id,
                    activityId: activity.id,
                    data,
                });
            }
            return archiveContact(accountId, contact.id, data);
        },
        onSuccess: () => {
            toast.success(
                activity?.id ? 'Successfully updated activity.' : 'Archived successfully.',
            );
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
            });
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const message = errors[key][0];
                    setError(key as FieldPath<ArchiveContactForm>, {
                        message,
                    });
                });
            } else {
                toast.error('Something went wrong.');
            }
        },
    });

    return (
        <AlertDialog.Root key={key} type={'warning'} {...props}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content className="max-w-[500px]">
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={handleSubmit(value => mutate(value))}
                            className="flex flex-col gap-4"
                        >
                            <AlertDialog.Header>
                                <AlertDialog.Icon customIcon={faArchive} />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <div className="flex flex-col gap-4">
                                    {activity?.id ? (
                                        <AlertDialog.Title>Edit activity</AlertDialog.Title>
                                    ) : (
                                        <>
                                            <AlertDialog.Title>Archive contact?</AlertDialog.Title>
                                            <AlertDialog.Description>
                                                This action hides the contact record from view, and
                                                can be reversed at any time. You also have the
                                                option to note a reason for archiving that will be
                                                added to the contact record's activity timeline.
                                            </AlertDialog.Description>
                                        </>
                                    )}
                                    <Form.Container>
                                        <Form.Label name={path('reason')}>Reason</Form.Label>
                                        <Form.SingleSelect control={control} name="reason">
                                            <SingleSelect.Trigger aria-label="reason">
                                                <SingleSelect.Value placeholder="Select reason" />
                                            </SingleSelect.Trigger>
                                            <SingleSelect.Content>
                                                {Object.values(ARCHIVE_REASONS).map(reason => (
                                                    <SingleSelect.Item
                                                        key={reason.value}
                                                        value={reason.value}
                                                    >
                                                        <SingleSelect.ItemText>
                                                            {reason.label}
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                ))}
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                        <Form.Error name={path('reason')} />
                                    </Form.Container>
                                    <Form.Container>
                                        <Form.Textarea
                                            control={control}
                                            name="note"
                                            rows={8}
                                            placeholder="Notes"
                                        />
                                        <Form.Error name={path('note')} />
                                    </Form.Container>
                                </div>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <Form.SubmitButton className="flex-1">
                                    {activity?.id ? 'Save' : 'Archive'}
                                </Form.SubmitButton>
                            </AlertDialog.Footer>
                        </form>
                    </Form.Provider>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = ArchiveContact;
const Trigger = AlertDialog.Trigger;
const ArchiveContactTrigger = Trigger;

export {
    Root,
    Trigger,
    //
    ArchiveContact,
    ArchiveContactTrigger,
};
